import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  BeakerIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentListIcon,
  TrashIcon,
  BellAlertIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { EF_THEME_COLORS } from '../constants/uiConstants';
import { Spinner } from './ui';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TestActionItem {
  id: string;
  title: string;
  assigned_to: string | null;
  due_date: string | null;
  nudge_context: string | null;
  priority: string;
}

interface TestSummaryData {
  summary_text: string | null;
  ef_themes: string[];
  coach_notes: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  generated_at: string | null;
  questionnaire: GeneratedQuestion[] | null;
}

interface TestRecord {
  id: string;
  label: string;
  coach_id: string;
  client_name: string;
  session_date: string | null;
  transcript_text: string;
  tester_comments: string | null;
  created_at: string;
  summary: TestSummaryData;
  action_items: TestActionItem[];
}

interface GeneratedQuestion {
  text: string;
  type: 'rating' | 'yesno' | 'freetext';
}

interface ListItem {
  id: string;
  label: string;
  client_name: string;
  session_date: string | null;
  created_at: string;
  ef_themes: string[];
  generated_at: string | null;
}

type View = 'list' | 'generate' | 'result';
type ResultTab = 'overview' | 'action_items' | 'coach_notes' | 'ef_themes' | 'questionnaire' | 'comments';

// ── Helpers ───────────────────────────────────────────────────────────────────

const SummaryMarkdown: React.FC<{ text: string }> = ({ text }) => (
  <ReactMarkdown
    components={{
      h1: ({ children }) => <h1 className="text-lg font-bold text-white mb-2">{children}</h1>,
      h2: ({ children }) => <h2 className="text-base font-semibold text-primary-300 mt-4 mb-1.5">{children}</h2>,
      h3: ({ children }) => <h3 className="text-sm font-semibold text-gray-300 mt-3 mb-1">{children}</h3>,
      p: ({ children }) => <p className="text-sm text-gray-300 mb-2 leading-relaxed">{children}</p>,
      ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-2">{children}</ul>,
      li: ({ children }) => <li className="text-sm text-gray-300">{children}</li>,
      strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
      em: ({ children }) => <em className="text-gray-400 italic">{children}</em>,
    }}
  >
    {text}
  </ReactMarkdown>
);

function formatDate(iso: string): string {
  // Date-only strings (YYYY-MM-DD) must be parsed as local time, not UTC,
  // otherwise they shift back one day in negative-offset timezones (e.g. EST).
  const date = /^\d{4}-\d{2}-\d{2}$/.test(iso)
    ? new Date(iso + 'T00:00:00')
    : new Date(iso);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const ASSIGNED_LABELS: Record<string, string> = { client: 'Client', coach: 'Coach', both: 'Both' };
const PRIORITY_COLORS: Record<string, string> = {
  high: 'text-red-400',
  medium: 'text-yellow-400',
  low: 'text-green-400',
};


// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  coachId: string;
  authToken: string;
  isAdmin?: boolean;
}

const TestSummaryPage: React.FC<Props> = ({ coachId, authToken, isAdmin = false }) => {
  const [view, setView] = useState<View>('list');
  const [list, setList] = useState<ListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [result, setResult] = useState<TestRecord | null>(null);

  const [label, setLabel] = useState('');
  const [clientName, setClientName] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [transcript, setTranscript] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ResultTab>('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [questions, setQuestions] = useState<GeneratedQuestion[] | null>(null);
  const [generatingQ, setGeneratingQ] = useState(false);
  const [qError, setQError] = useState<string | null>(null);
  const [testerComments, setTesterComments] = useState('');
  const [savingComments, setSavingComments] = useState(false);
  const [nudgeEditId, setNudgeEditId] = useState<string | null>(null);
  const [collapsedClients, setCollapsedClients] = useState<Set<string>>(new Set());

  // Group list by client name (case-insensitive), sessions sorted by session_date asc.
  // Groups sorted by most recent session date desc.
  const groupedList = useMemo(() => {
    const map = new Map<string, ListItem[]>();
    for (const item of list) {
      const key = item.client_name.toLowerCase();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.values())
      .map(items => ({
        client_name: items[0].client_name,
        items: [...items].sort((a, b) => (a.session_date ?? '').localeCompare(b.session_date ?? '')),
      }))
      .sort((a, b) => {
        const aLatest = a.items[a.items.length - 1].session_date ?? '';
        const bLatest = b.items[b.items.length - 1].session_date ?? '';
        return bLatest.localeCompare(aLatest);
      });
  }, [list]);

  const toggleClient = (name: string) =>
    setCollapsedClients(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  const [nudgeDraft, setNudgeDraft] = useState('');
  const [savingNudge, setSavingNudge] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` };

  const fetchList = async () => {
    setLoadingList(true);
    try {
      const url = isAdmin
        ? `${config.apiBaseUrl}/admin/test-summary/coach/${coachId}`
        : `${config.apiBaseUrl}/test-summary/coach/${coachId}`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setList(data.tests);
      }
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { fetchList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setTranscript(ev.target?.result as string ?? '');
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleGenerate = async () => {
    setGenError(null);
    if (!label.trim()) { setGenError('Label is required.'); return; }
    if (!clientName.trim()) { setGenError('Client name is required.'); return; }
    if (!sessionDate) { setGenError('Session date is required.'); return; }
    if (!transcript.trim()) { setGenError('Transcript is required.'); return; }

    setGenerating(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}/test-summary/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          coach_id: coachId,
          label: label.trim(),
          transcript_text: transcript,
          client_name: clientName.trim(),
          session_date: sessionDate,
          use_rag: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setGenError(data.detail ?? 'Generation failed.'); return; }
      setResult(data);
      setTesterComments(data.tester_comments ?? '');
      setActiveTab('overview');
      setCollapsed(false);
      setQuestions(data.summary.questionnaire ?? null);
      setQError(null);
      setView('result');
      fetchList();
    } catch {
      setGenError('Network error. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewSaved = async (id: string) => {
    const url = isAdmin
      ? `${config.apiBaseUrl}/admin/test-summary/${id}`
      : `${config.apiBaseUrl}/test-summary/${id}`;
    const res = await fetch(url, { headers });
    if (res.ok) {
      const data = await res.json();
      setResult(data);
      setTesterComments(data.tester_comments ?? '');
      setActiveTab('overview');
      setCollapsed(false);
      setQuestions(data.summary.questionnaire ?? null);
      setQError(null);
      setView('result');
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this test summary? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await fetch(`${config.apiBaseUrl}/test-summary/${id}`, { method: 'DELETE', headers });
      if (result?.id === id) { setResult(null); setView('list'); }
      fetchList();
    } finally {
      setDeletingId(null);
    }
  };

  const handleGenerateQuestionnaire = async (transcriptId: string) => {
    setGeneratingQ(true);
    setQError(null);
    try {
      const res = await fetch(`${config.apiBaseUrl}/test-summary/${transcriptId}/generate-questionnaire`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (!res.ok) { setQError(data.detail ?? 'Failed to generate questionnaire.'); return; }
      setQuestions(data.questions);
    } catch {
      setQError('Network error. Please try again.');
    } finally {
      setGeneratingQ(false);
    }
  };

  const goToGenerate = () => {
    setLabel(''); setClientName(''); setSessionDate('');
    setTranscript(''); setGenError(null);
    setView('generate');
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex-1 overflow-y-auto">

      {/* ── List view ── */}
      {view === 'list' && (
        <div className="p-8 max-w-4xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <BeakerIcon className="w-6 h-6 text-primary-400" />
                Test Summary Generation
              </h1>
              <p className="text-sm text-dark-muted mt-1">
                Run the summarizer on any transcript to test the output.
              </p>
            </div>
            {!isAdmin && (
              <button
                onClick={goToGenerate}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium rounded-lg transition-colors"
              >
                + New Test
              </button>
            )}
          </div>

          {loadingList ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : list.length === 0 ? (
            <div className="text-center py-16 text-dark-muted">
              <BeakerIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No tests yet. Click <strong>+ New Test</strong> to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedList.map((group) => {
                const isCollapsed = collapsedClients.has(group.client_name.toLowerCase());
                return (
                  <div key={group.client_name} className="border border-dark-border rounded-xl overflow-hidden">
                    {/* Client header */}
                    <button
                      onClick={() => toggleClient(group.client_name.toLowerCase())}
                      className="w-full flex items-center justify-between px-4 py-3 bg-dark-card hover:bg-dark-canvas transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="text-sm font-semibold text-white">{group.client_name}</span>
                        <span className="text-xs text-dark-muted">{group.items.length} session{group.items.length !== 1 ? 's' : ''}</span>
                      </div>
                      {isCollapsed
                        ? <ChevronRightIcon className="w-4 h-4 text-dark-muted" />
                        : <ChevronDownIcon className="w-4 h-4 text-dark-muted" />
                      }
                    </button>

                    {/* Sessions */}
                    {!isCollapsed && (
                      <div className="divide-y divide-dark-border">
                        {group.items.map((item) => (
                          <div key={item.id} className="flex items-center bg-dark-canvas hover:bg-dark-card transition-colors">
                            <button
                              onClick={() => handleViewSaved(item.id)}
                              className="flex-1 text-left px-4 py-3 min-w-0"
                            >
                              <div className="flex items-center gap-3">
                                <p className="text-sm font-medium text-white truncate">{item.label}</p>
                                {item.session_date && (
                                  <span className="text-xs text-dark-muted flex items-center gap-1 flex-shrink-0">
                                    <ClockIcon className="w-3 h-3" />
                                    {formatDate(item.session_date)}
                                  </span>
                                )}
                              </div>
                            </button>
                            <div className="flex items-center gap-1 px-3 flex-shrink-0">
                              <button
                                onClick={(e) => handleDelete(item.id, e)}
                                disabled={deletingId === item.id}
                                className="p-1.5 rounded hover:bg-red-900/30 text-dark-muted hover:text-red-400 transition-colors disabled:opacity-50"
                                title="Delete"
                              >
                                <TrashIcon className="w-3.5 h-3.5" />
                              </button>
                              <ChevronRightIcon className="w-4 h-4 text-dark-muted" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Generate view ── */}
      {view === 'generate' && (
        <div className="p-8 max-w-3xl">
          <button
            onClick={() => setView('list')}
            className="flex items-center gap-1.5 text-sm text-dark-muted hover:text-gray-300 mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" /> Back to tests
          </button>

          <h1 className="text-xl font-bold text-white mb-6">New Test Summary</h1>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Label <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. March session — task initiation test"
                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2.5 text-sm text-white placeholder-dark-muted focus:outline-none focus:border-primary-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Client Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g. Alex"
                  className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2.5 text-sm text-white placeholder-dark-muted focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Session Date <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                  className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-300">
                  Transcript <span className="text-red-400">*</span>
                </label>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 transition-colors"
                >
                  <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                  Upload .txt file
                </button>
              </div>
              <input ref={fileInputRef} type="file" accept=".txt" className="hidden" onChange={handleFileUpload} />
              <textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder="Paste transcript here or upload a .txt file…"
                rows={12}
                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2.5 text-sm text-white placeholder-dark-muted focus:outline-none focus:border-primary-500 resize-y font-mono"
              />
              {transcript && (
                <p className="text-xs text-dark-muted mt-1">
                  {transcript.trim().split(/\s+/).length.toLocaleString()} words
                </p>
              )}
            </div>

            {genError && (
              <div className="bg-red-900/30 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
                {genError}
              </div>
            )}

            <div className="flex gap-3 items-center">
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
              >
                {generating ? <><Spinner /> Generating…</> : <><BeakerIcon className="w-4 h-4" /> Generate Summary</>}
              </button>
              <button
                onClick={() => setView('list')}
                disabled={generating}
                className="px-4 py-2.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              {generating && (
                <p className="text-xs text-dark-muted">Typically takes 15–30 seconds…</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Result view ── */}
      {view === 'result' && result && (() => {
        const s = result.summary;
        const tokens = (s.prompt_tokens ?? 0) + (s.completion_tokens ?? 0);
        const tabs: { key: ResultTab; label: string }[] = [
          { key: 'overview',      label: 'Overview' },
          { key: 'action_items',  label: `Action Items/Nudges (${result.action_items.length})` },
          { key: 'coach_notes',   label: 'Coach Notes' },
          { key: 'ef_themes',     label: 'EF Themes' },
          { key: 'questionnaire', label: 'Tracker' },
          { key: 'comments',      label: 'Test Comments' },
        ];
        return (
          <div className="p-8 max-w-4xl">
            <button
              onClick={() => setView('list')}
              className="flex items-center gap-1.5 text-sm text-dark-muted hover:text-gray-300 mb-6 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" /> Back to tests
            </button>

            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-xl font-bold text-white">{result.label}</h1>
                <div className="flex items-center gap-3 mt-1 text-xs text-dark-muted flex-wrap">
                  <span>Client: {result.client_name}</span>
                  {result.session_date && <span className="flex items-center gap-1"><ClockIcon className="w-3 h-3" />{formatDate(result.session_date)}</span>}
                  {tokens > 0 && (
                    <span>{tokens.toLocaleString()} tokens ({s.prompt_tokens?.toLocaleString()} in · {s.completion_tokens?.toLocaleString()} out)</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 mt-1">
                <button
                  onClick={(e) => handleDelete(result.id, e)}
                  disabled={deletingId === result.id}
                  className="flex items-center gap-1.5 text-xs text-dark-muted hover:text-red-400 transition-colors disabled:opacity-50"
                  title="Delete this test"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                  Delete
                </button>
                <button
                  onClick={() => setCollapsed(c => !c)}
                  className="flex items-center gap-1.5 text-xs text-dark-muted hover:text-gray-300 transition-colors"
                >
                  {collapsed ? <><ChevronDownIcon className="w-4 h-4" /> Expand</> : <><ChevronUpIcon className="w-4 h-4" /> Collapse</>}
                </button>
              </div>
            </div>

            {!collapsed && (
              <>
                {/* Tab bar */}
                <div className="border-b border-dark-border mb-6">
                  <nav className="-mb-px flex space-x-6 overflow-x-auto">
                    {tabs.map(({ key, label: tabLabel }) => (
                      <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className={`whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${
                          activeTab === key
                            ? 'border-violet-500 text-violet-400'
                            : 'border-transparent text-gray-500 hover:text-gray-300'
                        }`}
                      >
                        {tabLabel}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Overview */}
                {activeTab === 'overview' && (
                  <div>
                    {s.summary_text
                      ? <SummaryMarkdown text={s.summary_text} />
                      : <p className="text-sm text-dark-muted italic">No summary generated.</p>}
                  </div>
                )}

                {/* Action Items */}
                {activeTab === 'action_items' && (
                  <div>
                    {result.action_items.length === 0 ? (
                      <p className="text-sm text-dark-muted italic">No action items identified.</p>
                    ) : (
                      <ul className="space-y-2">
                        {result.action_items.map((item) => {
                          const isEditingNudge = nudgeEditId === item.id;
                          return (
                            <li key={item.id} className="rounded-lg bg-dark-card border border-dark-border overflow-hidden">
                              <div className="flex gap-3 p-3">
                                <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5 bg-violet-500" />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <p className="text-sm font-medium text-gray-200">{item.title}</p>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      {item.nudge_context ? (
                                        <>
                                          <button
                                            onClick={() => { setNudgeEditId(item.id); setNudgeDraft(item.nudge_context ?? ''); }}
                                            title="Edit nudge"
                                            className="p-1 rounded text-amber-400 hover:bg-amber-900/20 transition-colors"
                                          >
                                            <PencilIcon className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={async () => {
                                              await fetch(`${config.apiBaseUrl}/test-summary/${result.id}/action-items/${item.id}/nudge`, {
                                                method: 'PATCH', headers,
                                                body: JSON.stringify({ nudge_context: null }),
                                              });
                                              setResult(r => r ? { ...r, action_items: r.action_items.map(a => a.id === item.id ? { ...a, nudge_context: null } : a) } : r);
                                            }}
                                            title="Remove nudge"
                                            className="p-1 rounded text-gray-500 hover:text-red-400 hover:bg-red-900/20 transition-colors"
                                          >
                                            <TrashIcon className="w-3.5 h-3.5" />
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          onClick={() => { setNudgeEditId(item.id); setNudgeDraft(''); }}
                                          title="Add nudge"
                                          className="p-1 rounded text-gray-500 hover:text-amber-400 hover:bg-amber-900/20 transition-colors"
                                        >
                                          <PlusIcon className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {item.due_date && (
                                    <p className="text-xs text-dark-muted mt-0.5">Due {item.due_date}</p>
                                  )}
                                  {item.nudge_context && !isEditingNudge && (
                                    <p className="text-xs text-amber-400/70 mt-1 italic flex items-center gap-1">
                                      <BellAlertIcon className="w-3 h-3 flex-shrink-0" />{item.nudge_context}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2 mt-1">
                                    {item.assigned_to && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-dark-hover text-gray-400 capitalize">
                                        {ASSIGNED_LABELS[item.assigned_to] ?? item.assigned_to}
                                      </span>
                                    )}
                                    <span className={`text-xs font-medium capitalize ${PRIORITY_COLORS[item.priority] ?? 'text-gray-400'}`}>
                                      {item.priority}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {isEditingNudge && (
                                <div className="px-3 pb-3 pt-1 border-t border-amber-900/30 bg-amber-950/20">
                                  <div className="flex gap-2 items-center">
                                    <BellAlertIcon className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                                    <input
                                      autoFocus
                                      type="text"
                                      value={nudgeDraft}
                                      onChange={e => setNudgeDraft(e.target.value)}
                                      placeholder="Nudge message…"
                                      className="flex-1 text-xs bg-dark-canvas border border-dark-border rounded px-2 py-1.5 text-gray-200 focus:outline-none focus:border-amber-400"
                                    />
                                    <button
                                      onClick={async () => {
                                        setSavingNudge(true);
                                        await fetch(`${config.apiBaseUrl}/test-summary/${result.id}/action-items/${item.id}/nudge`, {
                                          method: 'PATCH', headers,
                                          body: JSON.stringify({ nudge_context: nudgeDraft || null }),
                                        });
                                        setResult(r => r ? { ...r, action_items: r.action_items.map(a => a.id === item.id ? { ...a, nudge_context: nudgeDraft || null } : a) } : r);
                                        setNudgeEditId(null);
                                        setSavingNudge(false);
                                      }}
                                      disabled={savingNudge}
                                      className="p-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white transition-colors"
                                    >
                                      <CheckIcon className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => setNudgeEditId(null)}
                                      className="p-1.5 rounded text-gray-400 hover:text-gray-200 hover:bg-dark-hover transition-colors"
                                    >
                                      <XMarkIcon className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}

                {/* Nudges */}
                {/* Coach Notes */}
                {activeTab === 'coach_notes' && (
                  <div>
                    {s.coach_notes
                      ? <SummaryMarkdown text={s.coach_notes} />
                      : <p className="text-sm text-dark-muted italic">No coach notes generated.</p>}
                  </div>
                )}

                {/* EF Themes */}
                {activeTab === 'ef_themes' && (
                  <div>
                    {s.ef_themes.length > 0 ? (
                      <div className="space-y-4">
                        <p className="text-sm text-gray-400">EF domains active in this session:</p>
                        <div className="flex flex-wrap gap-2">
                          {s.ef_themes.map((theme) => (
                            <span
                              key={theme}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${EF_THEME_COLORS[theme] ?? 'bg-gray-100 text-gray-700'}`}
                            >
                              {theme}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-dark-muted italic">No EF themes identified.</p>
                    )}
                  </div>
                )}

                {/* Questionnaire */}
                {activeTab === 'questionnaire' && (
                  <div>
                    {!questions && (
                      <div className="text-center py-10">
                        <ClipboardDocumentListIcon className="w-10 h-10 mx-auto mb-3 text-dark-muted opacity-50" />
                        <p className="text-sm text-gray-400 mb-4">
                          Generate a set of weekly check-in questions tailored to this session's themes and action items.
                        </p>
                        {qError && (
                          <p className="text-sm text-red-400 mb-4">{qError}</p>
                        )}
                        <button
                          onClick={() => handleGenerateQuestionnaire(result.id)}
                          disabled={generatingQ}
                          className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          {generatingQ ? <><Spinner /> Generating…</> : <><ClipboardDocumentListIcon className="w-4 h-4" /> Generate Tracker</>}
                        </button>
                        {generatingQ && (
                          <p className="text-xs text-dark-muted mt-3">Typically takes 5–10 seconds…</p>
                        )}
                      </div>
                    )}

                    {questions && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm text-gray-400">{questions.length} questions generated</p>
                          <button
                            onClick={() => { setQuestions(null); setQError(null); }}
                            className="text-xs text-dark-muted hover:text-gray-300 transition-colors"
                          >
                            Regenerate
                          </button>
                        </div>
                        {questions.map((q, i) => (
                          <div key={i} className="flex gap-3 p-3 rounded-lg bg-dark-card border border-dark-border">
                            <span className="text-xs font-bold text-violet-400 mt-0.5 w-5 flex-shrink-0">{i + 1}.</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-200">{q.text}</p>
                              <span className={`inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full font-medium ${
                                q.type === 'rating'   ? 'bg-blue-100 text-blue-800' :
                                q.type === 'yesno'    ? 'bg-green-100 text-green-800' :
                                                        'bg-yellow-100 text-yellow-800'
                              }`}>
                                {q.type === 'rating' ? '1–5 Rating' : q.type === 'yesno' ? 'Yes / No' : 'Free Text'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {activeTab === 'comments' && (
                  <div>
                    <p className="text-xs text-dark-muted mb-3">Capture your feedback on this test run — what worked, what didn't, anything to follow up on.</p>
                    <textarea
                      value={testerComments}
                      onChange={e => setTesterComments(e.target.value)}
                      rows={6}
                      placeholder="Add your notes here…"
                      className="w-full bg-dark-card border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-dark-muted resize-none focus:outline-none focus:border-primary-500"
                    />
                    <button
                      onClick={async () => {
                        setSavingComments(true);
                        await fetch(`${config.apiBaseUrl}/test-summary/${result.id}/comments`, {
                          method: 'PATCH',
                          headers,
                          body: JSON.stringify({ comments: testerComments }),
                        });
                        setSavingComments(false);
                      }}
                      disabled={savingComments}
                      className="mt-2 px-4 py-1.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                    >
                      {savingComments ? 'Saving…' : 'Save Comments'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

    </div>
  );
};

export default TestSummaryPage;
